import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin, Sparkles, Shield, Heart } from 'lucide-react';
import { galleryImages, testimonials } from '../data/mock';
import { useSettings } from '../context/SettingsContext';
import { getServices } from '../services/api';
import LazyImage from '../components/LazyImage';

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { businessInfo } = useSettings();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const popularServices = services.filter(s => s.is_popular);
  const featuredGallery = galleryImages.slice(0, 6);
  const featuredTestimonials = testimonials.slice(0, 3);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] lg:min-h-[85vh] flex items-center bg-[var(--bg-primary)]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left">
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-4">
                Home-Based Nail Studio
              </p>
              <h1 className="hero-large mb-5 sm:mb-6">
                Luxury Nail Art,<br />
                <span className="text-[var(--text-secondary)]">Cozy Home Comfort</span>
              </h1>
              <p className="body-large text-[var(--text-secondary)] mb-8 max-w-md mx-auto lg:mx-0">
                Experience premium nail artistry in a relaxed, private setting. 
                Personalized service and exceptional quality await you.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/book" className="btn-primary">
                  Book Appointment
                  <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-secondary">
                  View Services
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex items-center justify-center lg:justify-start gap-6 sm:gap-8 mt-10 sm:mt-12 pt-8 border-t border-[var(--border-light)]">
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-medium text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>5+</p>
                  <p className="body-small text-[var(--text-light)]">Years</p>
                </div>
                <div className="w-px h-10 bg-[var(--border-light)]" />
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-medium text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>500+</p>
                  <p className="body-small text-[var(--text-light)]">Clients</p>
                </div>
                <div className="w-px h-10 bg-[var(--border-light)]" />
                <div className="text-center flex flex-col items-center">
                  <div className="flex items-center gap-1">
                    <Star size={18} className="fill-[var(--accent-nude-dark)] text-[var(--accent-nude-dark)]" />
                    <p className="text-2xl sm:text-3xl font-medium text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>5.0</p>
                  </div>
                  <p className="body-small text-[var(--text-light)]">Rating</p>
                </div>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="order-1 lg:order-2">
              <div className="relative max-w-md mx-auto lg:max-w-none">
                <div className="rounded-xl lg:rounded-2xl overflow-hidden">
                  <LazyImage
                    src={galleryImages[0].url}
                    alt="Elegant nail art"
                    className="w-full"
                    aspectRatio="4/5"
                    priority={true}
                  />
                </div>
                {/* Booking badge */}
                <div className="absolute -bottom-4 left-4 sm:left-6 bg-[var(--bg-card)] px-5 py-4 rounded-xl shadow-sm hidden sm:block">
                  <p className="body-small text-[var(--text-light)] mb-1">Now Booking</p>
                  <p className="heading-3">August 2025</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-[var(--bg-secondary)] content-visibility-auto">
        <div className="container">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Services</p>
            <h2 className="heading-1 mb-3">Popular Treatments</h2>
            <p className="body-regular text-[var(--text-secondary)] max-w-md mx-auto">
              Discover our most loved nail services, crafted with care and premium products.
            </p>
          </div>
          
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[var(--bg-card)] rounded-xl overflow-hidden">
                  <div className="aspect-[4/3] skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 skeleton w-2/3" />
                    <div className="h-4 skeleton w-full" />
                    <div className="h-4 skeleton w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {popularServices.map((service) => (
                <Link 
                  key={service.id} 
                  to={`/book?service=${service.id}`}
                  className="bg-[var(--bg-card)] rounded-xl overflow-hidden transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="overflow-hidden">
                    <LazyImage
                      src={service.image_url}
                      alt={service.name}
                      className="w-full transition-transform duration-500 hover:scale-105"
                      aspectRatio="4/3"
                    />
                  </div>
                  <div className="p-5 sm:p-6">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h3 className="heading-3">{service.name}</h3>
                      <span className="text-lg font-medium text-[var(--text-heading)] whitespace-nowrap" style={{fontFamily: "'Cormorant Garamond', serif"}}>
                        ${service.price}
                      </span>
                    </div>
                    <p className="body-small text-[var(--text-secondary)] mb-3 line-clamp-2">{service.description}</p>
                    <p className="body-small text-[var(--text-light)]">{service.duration}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          <div className="text-center mt-10">
            <Link to="/services" className="btn-secondary">
              View All Services
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding content-visibility-auto">
        <div className="container">
          <div className="text-center max-w-xl mx-auto mb-10 sm:mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Why HomeGlow</p>
            <h2 className="heading-1 mb-3">A Different Kind of Experience</h2>
            <p className="body-regular text-[var(--text-secondary)]">
              Professional nail care in a private, relaxing home studio setting.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: Heart, title: 'Private & Cozy', desc: 'One-on-one attention in a peaceful setting' },
              { icon: Sparkles, title: 'Premium Products', desc: 'High-quality, long-lasting products' },
              { icon: Shield, title: 'Hygienic & Safe', desc: 'Sterilized tools for every client' },
              { icon: MapPin, title: 'Locally Serving', desc: 'Kitchener, Waterloo & Cambridge' }
            ].map((item, i) => (
              <div key={i} className="text-center p-5 sm:p-6 bg-[var(--bg-card)] rounded-xl">
                <div className="inline-flex items-center justify-center w-12 h-12 mb-4 bg-[var(--accent-blush)] rounded-full">
                  <item.icon size={20} strokeWidth={1.5} className="text-[var(--text-primary)]" />
                </div>
                <h3 className="text-sm sm:text-base font-medium mb-2 text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>{item.title}</h3>
                <p className="body-small text-[var(--text-secondary)]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding bg-[var(--bg-secondary)] content-visibility-auto">
        <div className="container">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Gallery</p>
            <h2 className="heading-1 mb-3">Recent Work</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {featuredGallery.map((image) => (
              <Link 
                key={image.id} 
                to="/gallery"
                className="aspect-square overflow-hidden rounded-lg sm:rounded-xl group"
              >
                <LazyImage
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                  aspectRatio="1/1"
                />
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/gallery" className="btn-secondary">
              View Full Gallery
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding content-visibility-auto">
        <div className="container">
          <div className="text-center max-w-xl mx-auto mb-10 sm:mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">Testimonials</p>
            <h2 className="heading-1">What Clients Say</h2>
          </div>
          
          <div className="flex lg:grid lg:grid-cols-3 gap-4 sm:gap-6 overflow-x-auto hide-scrollbar scroll-touch -mx-5 px-5 lg:mx-0 lg:px-0">
            {featuredTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="p-6 sm:p-8 bg-[var(--bg-card)] rounded-xl flex-shrink-0 w-[85vw] sm:w-[70vw] lg:w-auto">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[var(--accent-nude-dark)] text-[var(--accent-nude-dark)]" />
                  ))}
                </div>
                <p className="body-regular text-[var(--text-secondary)] mb-5 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-medium text-[var(--text-heading)]">{testimonial.name}</p>
                  <p className="body-small text-[var(--text-light)]">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[var(--accent-blush)]">
        <div className="container">
          <div className="text-center max-w-lg mx-auto">
            <h2 className="hero-medium mb-4">Ready for Beautiful Nails?</h2>
            <p className="body-large text-[var(--text-secondary)] mb-8">
              Book your appointment today and experience the HomeGlow difference.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/book" className="btn-primary">
                Book Appointment
                <ArrowRight size={16} />
              </Link>
              <a
                href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary bg-white border-white hover:bg-[var(--bg-primary)]"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
